import { CdkOverlayOrigin, ConnectedOverlayPositionChange, OverlayModule } from '@angular/cdk/overlay';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DoCheck, ElementRef, EventEmitter, HostBinding, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription, catchError, debounce, of, tap, timer } from 'rxjs';
import { isRealChange } from '../../../helper/simple-changes-typed';
import { IsTouchOnlyEnvironment } from '../../../helper/mobile';
import { FormControlEx } from '../../forms/form-control';
import { preventNonInteractiveHostPointerFocus } from '../../forms/input-control.base';
import { AutoCompleteSelectionItem } from '../../autocomplete-remote/autocomplete-selection-item';
import { IAutoCompleteService } from '../../autocomplete-remote/autocomplete-service.interface';
import { AstralChipComponent } from '../astral-chip/astral-chip.component';
import {
  ASTRAL_DROPDOWN_OVERLAY_VIEWPORT_MARGIN,
  getAstralDropdownOverlayPositions,
  getAstralDropdownOverlayWidth,
  getAstralPopoverOverlayPositions,
} from '../astral-dropdown-overlay.util';
import { AstralFieldActionComponent } from '../astral-field-action/astral-field-action.component';
import { AstralFieldShellComponent } from '../astral-field-shell/astral-field-shell.component';
import { resolveAstralIconClass } from '../astral-icon-class.util';
import { AstralFieldAccessibilityDirective } from '../astral-field-accessibility.util';

/**
 * Async multi-select for API-backed selections. It keeps the remote data contract
 * used by legacy autocomplete controls while rendering selected values as Astral chips.
 */
@Component({
  selector: 'astral-remote-multi-select',
  standalone: true,
  imports: [ReactiveFormsModule, OverlayModule, TranslatePipe, AstralChipComponent, AstralFieldActionComponent, AstralFieldShellComponent, AstralFieldAccessibilityDirective],
  template: `
    <astral-field-shell [control]="control" [forId]="resolvedInputId" [helpText]="helpText">
      <div class="astral-remote-multi-select" [class.astral-remote-multi-select--readonly]="control?.isReadonly" [class.astral-remote-multi-select--disabled]="control?.disabled">
        <div class="astral-remote-multi-select__actions">
          @if (showClearOthers && !control?.disabled) {
            <astral-field-action icon="clearOthers" titleKey="COMMON.CLEAR_OTHER_FILTERS" ariaLabelKey="COMMON.CLEAR_OTHER_FILTERS" (click)="clearOtherInputs()"></astral-field-action>
          }
          @if (selectedItems.length && !control?.isReadonly && !control?.disabled) {
            <astral-field-action icon="clear" titleKey="COMMON.CLEAR" ariaLabelKey="COMMON.CLEAR" (click)="clearAll()"></astral-field-action>
          }
        </div>

        <div
          cdkOverlayOrigin
          #overlayOrigin="cdkOverlayOrigin"
          #controlShell
          class="astral-remote-multi-select__control-shell"
          [class.astral-remote-multi-select__control-shell--has-leading-content]="control?.isReadonly || selectedItems.length"
        >
          @if (control?.isReadonly) {
            <span class="astral-remote-multi-select__readonly-icon" aria-hidden="true">
              <i [class]="readonlyIconClass"></i>
            </span>
          }
          @if (selectedItems.length) {
            <div class="astral-remote-multi-select__chips" [class.astral-remote-multi-select__chips--scrollable]="showAllSelectedChips">
              @if (showSelectionSummary) {
                <button
                  cdkOverlayOrigin
                  #selectionSummaryOrigin="cdkOverlayOrigin"
                  #selectionSummaryTrigger
                  class="astral-remote-multi-select__selection-summary"
                  type="button"
                  [disabled]="isSelectionSummaryDisabled ? true : null"
                  [attr.aria-controls]="selectionSummaryPanelId"
                  [attr.aria-expanded]="isSelectionSummaryDisabled ? null : isSelectionSummaryOpen"
                  [attr.aria-label]="'COMMON.SELECTED_OPTIONS_COUNT' | translate: { count: selectedItems.length }"
                  [attr.title]="isSelectionSummaryDisabled ? null : (selectionSummaryTriggerLabelKey | translate)"
                  (click)="toggleSelectionSummary($event)"
                >
                  {{ 'COMMON.SELECTED_OPTIONS_COUNT' | translate: { count: selectedItems.length } }}
                </button>
              } @else {
                @for (item of selectedItems; track item) {
                  <astral-chip [label]="item.shortName || item.name" [removable]="canRemoveSelectedItems" [removeAriaLabelText]="('COMMON.REMOVE' | translate) + ': ' + (item.shortName || item.name)" (remove)="removeItem(item)"></astral-chip>
                }
              }
            </div>
          }
          <input
            #input
            astralFieldAccessibility
            class="astral-remote-multi-select__control"
            [class.astral-remote-multi-select__control--has-leading-content]="control?.isReadonly || selectedItems.length"
            [class.astral-remote-multi-select__control--selection-display]="showAllSelectedChips && selectedItems.length"
            type="text"
            autocomplete="off"
            role="combobox"
            [astralFieldControl]="control"
            [astralFieldHelpText]="helpText"
            [astralFieldInputId]="resolvedInputId"
            aria-autocomplete="list"
            aria-haspopup="listbox"
            [attr.id]="resolvedInputId"
            [attr.name]="resolvedInputName"
            [attr.aria-controls]="resolvedInputId + '-panel'"
            [attr.aria-activedescendant]="activeOptionId"
            [attr.aria-expanded]="isPanelOpen ? 'true' : 'false'"
            [attr.placeholder]="showSearchPlaceholder ? ('COMMON.TYPE_TO_SEARCH' | translate) : null"
            [formControl]="searchControl"
            [readonly]="control?.isReadonly ? true : null"
            (focus)="openPanel()"
            (blur)="handleSearchBlur()"
            (keydown)="handleSearchKeydown($event)"
          />
        </div>
      </div>
    </astral-field-shell>

    <ng-template cdkConnectedOverlay [cdkConnectedOverlayOrigin]="overlayOrigin" [cdkConnectedOverlayOpen]="isPanelOpen" [cdkConnectedOverlayPositions]="overlayPositions" [cdkConnectedOverlayWidth]="overlayWidth" [cdkConnectedOverlayPush]="false" [cdkConnectedOverlayFlexibleDimensions]="true" [cdkConnectedOverlayViewportMargin]="overlayViewportMargin" (overlayOutsideClick)="closePanel()" (positionChange)="handlePositionChange($event)">
      <div #panel class="astral-remote-multi-select__panel" [class.astral-remote-multi-select__panel--above]="isPanelAbove" [attr.id]="resolvedInputId + '-panel'" role="listbox" aria-multiselectable="true" [attr.aria-labelledby]="resolvedInputId" (scroll)="handlePanelScroll()">
        @for (item of displayItems; track item; let index = $index) {
          <button type="button" class="astral-remote-multi-select__option" role="option" [attr.id]="optionId(index)" [attr.tabindex]="-1" [class.astral-remote-multi-select__option--active]="index === activeOptionIndex" [class.astral-remote-multi-select__option--selected]="isSelected(item)" [attr.aria-selected]="isSelected(item)" (mousedown)="$event.preventDefault()" (click)="toggleItem(item)">
            <i [class]="checkboxIconClass(item)" aria-hidden="true"></i>
            <span>{{ item.name }}</span>
          </button>
        }
        @if (!displayItems.length) {
          <div class="astral-remote-multi-select__empty">{{ 'COMMON.SEARCH_RESULT_EMPTY' | translate }}</div>
        }
      </div>
    </ng-template>

    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="selectionSummaryOrigin"
      [cdkConnectedOverlayOpen]="isSelectionSummaryOpen"
      [cdkConnectedOverlayPositions]="selectionSummaryOverlayPositions"
      [cdkConnectedOverlayPush]="true"
      [cdkConnectedOverlayFlexibleDimensions]="true"
      [cdkConnectedOverlayViewportMargin]="overlayViewportMargin"
      (overlayOutsideClick)="closeSelectionSummary()"
    >
      <div #selectionSummaryPanel class="astral-remote-multi-select__selection-summary-panel" role="dialog" [attr.id]="selectionSummaryPanelId" [attr.aria-label]="'PLURAL_ITEMS_SELECTED_TEXT' | translate: { count: selectedItems.length }" (focusout)="handleSelectionSummaryFocusOut()" (keydown)="handleSelectionSummaryKeydown($event)" (mousedown)="handleSelectionSummaryMouseDown($event)">
        @for (item of selectedItems; track item) {
          <astral-chip [label]="item.shortName || item.name" [removable]="canRemoveSelectedItems" [removeAriaLabelText]="('COMMON.REMOVE' | translate) + ': ' + (item.shortName || item.name)" (remove)="removeItem(item)"></astral-chip>
        }
      </div>
    </ng-template>
  `,
  styleUrls: ['./astral-remote-multi-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AstralRemoteMultiSelectComponent<TData = any, TOptions = any> implements AfterViewInit, DoCheck, OnChanges, OnDestroy, OnInit {
  private static nextId = 0;
  private _control: FormControlEx;
  private readonly uid = `astral-remote-multi-select-${++AstralRemoteMultiSelectComponent.nextId}`;
  private readonly subscriptions = new Subscription();
  private selectionLoadSub = new Subscription();
  private loadSub: Subscription;
  private selectionSyncVersion = 0;
  private page = 0;
  private allPagesLoaded = false;
  private currentSearch = '';
  private resizeObserver?: ResizeObserver;
  private searchControlDisabled: boolean | undefined;

  @Input() set control(value: AbstractControl) {
    this._control = value as FormControlEx;
  }
  get control(): FormControlEx {
    return this._control;
  }
  @Input() service: IAutoCompleteService<TData, TOptions>;
  @Input() defaultSearch = '*';
  @Input() serviceOptions: TOptions;
  @Input() updateOnOptionsChange = false;
  @Input() clearOthers = false;
  @Input() helpText: string;
  @Input() inputId: string;
  @Input() inputName: string;
  @Input() maxVisibleChips = 1;
  @Output() readonly addonClick = new EventEmitter<FormControlEx>();
  @Output() readonly clearClick = new EventEmitter<FormControlEx>();

  @ViewChild('input') input: ElementRef<HTMLInputElement>;
  @ViewChild('controlShell') controlShell: ElementRef<HTMLElement>;
  @ViewChild('overlayOrigin') overlayOrigin: CdkOverlayOrigin;
  @ViewChild('selectionSummaryOrigin') selectionSummaryOrigin?: CdkOverlayOrigin;
  @ViewChild('selectionSummaryPanel') selectionSummaryPanel?: ElementRef<HTMLElement>;
  @ViewChild('selectionSummaryTrigger') selectionSummaryTrigger?: ElementRef<HTMLButtonElement>;

  searchControl = new FormControl('');
  selectedItems: AutoCompleteSelectionItem<TData>[] = [];
  searchResults: AutoCompleteSelectionItem<TData>[] = [];
  isPanelOpen = false;
  isPanelAbove = false;
  isSelectionSummaryOpen = false;
  private isMovingSelectionSummaryFocus = false;
  activeOptionIndex = -1;
  overlayWidth: number;
  readonly overlayViewportMargin = ASTRAL_DROPDOWN_OVERLAY_VIEWPORT_MARGIN;

  constructor(private readonly cd: ChangeDetectorRef, private readonly elementRef: ElementRef<HTMLElement>) {}

  @HostBinding('tabindex') tabindex = -1;

  @HostListener('focus')
  focusHost(): void {
    this.input?.nativeElement.focus();
  }

  @HostListener('pointerdown', ['$event'])
  handleHostPointerDown(event: PointerEvent): void {
    preventNonInteractiveHostPointerFocus(event);
  }

  @HostListener('window:resize')
  handleWindowResize(): void {
    if (this.isPanelOpen) requestAnimationFrame(() => this.syncOverlayWidth());
  }

  get resolvedInputId(): string {
    return this.inputId ?? this.uid;
  }

  get resolvedInputName(): string {
    if (this.inputName) {
      return this.inputName;
    }

    const caption = this.control?.caption?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return caption || this.uid;
  }

  get overlayPositions() {
    return getAstralDropdownOverlayPositions();
  }

  get selectionSummaryOverlayPositions() {
    return getAstralPopoverOverlayPositions();
  }

  get displayItems(): AutoCompleteSelectionItem<TData>[] {
    const resultValues = new Set(this.searchResults.map(item => item.value));
    return [...this.searchResults, ...this.selectedItems.filter(item => !resultValues.has(item.value))];
  }

  get showSelectionSummary(): boolean {
    return !this.showAllSelectedChips && (this.selectedItems.length > this.maxVisibleChips || (this.isSelectionSummaryOpen && this.selectedItems.length > 0));
  }

  get showAllSelectedChips(): boolean {
    return !!this.control?.isReadonly || !!this.control?.disabled;
  }

  get canRemoveSelectedItems(): boolean {
    return !this.control?.isReadonly && !this.control?.disabled;
  }

  get isSelectionSummaryDisabled(): boolean {
    return !!this.control?.disabled;
  }

  @HostListener('document:keydown.escape', ['$event'])
  handleEscape(event: Event): void {
    if (this.isSelectionSummaryOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.closeSelectionSummary(false, true);
      return;
    }

    if (this.isPanelOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.closePanel(true);
    }
  }

  get selectionSummaryTriggerLabelKey(): string {
    return IsTouchOnlyEnvironment() ? 'COMMON.TAP_TO_SHOW_SELECTED_OPTIONS' : 'COMMON.CLICK_TO_SHOW_SELECTED_OPTIONS';
  }

  get selectionSummaryPanelId(): string {
    return `${this.resolvedInputId}-selection-summary`;
  }

  get activeOptionId(): string | null {
    return this.activeOptionIndex >= 0 ? this.optionId(this.activeOptionIndex) : null;
  }

  get readonlyIconClass(): string {
    return resolveAstralIconClass('lock') ?? '';
  }

  get showSearchPlaceholder(): boolean {
    return !this.control?.isReadonly && !this.control?.disabled;
  }

  get showClearOthers(): boolean {
    return this.clearOthers && !!this.control?.parent && Object.keys(this.control.parent.controls).length > 1 && this.selectedItems.length > 0;
  }

  checkboxIconClass(item: AutoCompleteSelectionItem<TData>): string {
    return resolveAstralIconClass(this.isSelected(item) ? 'checkboxChecked' : 'checkboxUnchecked') ?? '';
  }

  ngOnInit(): void {
    this.syncSelectedItemsFromControl(this.control?.value);
    this.subscriptions.add(this.control.valueChanges.subscribe(value => this.syncSelectedItemsFromControl(value)));
    this.syncSearchControlDisabledState();
    this.subscriptions.add(
      this.searchControl.valueChanges
        .pipe(
          tap(() => this.loadSub?.unsubscribe()),
          debounce(value => value ? timer(300) : timer(0))
        )
        .subscribe(value => this.loadResults(value || this.defaultSearch))
    );
  }

  ngDoCheck(): void {
    // Some existing forms intentionally toggle controls with emitEvent: false.
    this.syncSearchControlDisabledState();
    if (this.showAllSelectedChips && this.isSelectionSummaryOpen) {
      this.closeSelectionSummary();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!isRealChange(changes.serviceOptions)) return;
    this.clearCachedResults();
    this.loadResults(this.currentSearch || this.defaultSearch);
    this.syncSelectedItemsFromControl(this.control?.value, this.updateOnOptionsChange);
  }

  ngAfterViewInit(): void {
    this.syncOverlayWidth();
    if (typeof ResizeObserver !== 'undefined' && this.controlShell?.nativeElement) {
      this.resizeObserver = new ResizeObserver(() => this.syncOverlayWidth());
      this.resizeObserver.observe(this.controlShell.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.subscriptions.unsubscribe();
    this.selectionLoadSub.unsubscribe();
    this.loadSub?.unsubscribe();
  }

  openPanel(): void {
    if (this.control?.isReadonly || this.control?.disabled) return;
    this.closeSelectionSummary();
    this.isPanelOpen = true;
    this.loadResults(this.searchControl.value || this.defaultSearch);
    requestAnimationFrame(() => this.syncOverlayWidth());
  }

  closePanel(restoreInputFocus = false): void {
    this.isPanelOpen = false;
    this.activeOptionIndex = -1;
    if (restoreInputFocus) {
      requestAnimationFrame(() => this.input?.nativeElement.focus());
    }
  }

  handleSearchKeydown(event: KeyboardEvent): void {
    if (this.control?.isReadonly || this.control?.disabled) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closePanel(true);
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!this.isPanelOpen) {
        this.openPanel();
      }

      const itemCount = this.displayItems.length;
      if (!itemCount) {
        return;
      }

      const direction = event.key === 'ArrowDown' ? 1 : -1;
      if (this.activeOptionIndex < 0) {
        this.activeOptionIndex = direction === 1 ? 0 : itemCount - 1;
      } else {
        this.activeOptionIndex = (this.activeOptionIndex + direction + itemCount) % itemCount;
      }
      requestAnimationFrame(() => this.scrollActiveOptionIntoView());
      return;
    }

    if (event.key === 'Enter' && this.activeOptionIndex >= 0) {
      const activeItem = this.displayItems[this.activeOptionIndex];
      if (!activeItem) {
        return;
      }

      event.preventDefault();
      this.toggleItem(activeItem);
    }
  }

  handleSearchBlur(): void {
    queueMicrotask(() => {
      if (this.elementRef.nativeElement.ownerDocument?.activeElement === this.input?.nativeElement) {
        return;
      }

      this.control?.markAsTouched();
      this.closePanel();
    });
  }

  toggleSelectionSummary(event: MouseEvent): void {
    if (this.control?.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    this.isSelectionSummaryOpen = !this.isSelectionSummaryOpen;
    if (this.isSelectionSummaryOpen) {
      requestAnimationFrame(() => this.focusFirstSummaryAction());
    }
  }

  closeSelectionSummary(restoreTriggerFocus = false, restoreInputFocus = false): void {
    this.isSelectionSummaryOpen = false;
    if (restoreTriggerFocus) {
      requestAnimationFrame(() => this.selectionSummaryTrigger?.nativeElement.focus());
    } else if (restoreInputFocus) {
      requestAnimationFrame(() => this.input?.nativeElement.focus());
    }
  }

  handlePositionChange(change: ConnectedOverlayPositionChange): void {
    this.isPanelAbove = change.connectionPair.originY === 'top';
  }

  handlePanelScroll(): void {
    const panel = this.panel?.nativeElement;
    if (!panel || this.allPagesLoaded || panel.scrollTop + panel.clientHeight < panel.scrollHeight - 24) return;
    this.loadResults(this.currentSearch || this.defaultSearch, this.page + 1);
  }

  @ViewChild('panel') panel: ElementRef<HTMLElement>;

  isSelected(item: AutoCompleteSelectionItem<TData>): boolean {
    return this.selectedItems.some(selected => selected.value === item.value);
  }

  toggleItem(item: AutoCompleteSelectionItem<TData>): void {
    if (this.control?.isReadonly || this.control?.disabled) return;
    this.selectedItems = this.isSelected(item) ? this.selectedItems.filter(selected => selected.value !== item.value) : [...this.selectedItems, item];
    if (!this.showSelectionSummary) this.closeSelectionSummary();
    this.searchControl.setValue('');
    this.updateControlValue();
  }

  handleSelectionSummaryFocusOut(): void {
    queueMicrotask(() => {
      if (this.isMovingSelectionSummaryFocus) {
        return;
      }

      const activeElement = this.elementRef.nativeElement.ownerDocument?.activeElement;
      if (activeElement && this.selectionSummaryPanel?.nativeElement.contains(activeElement)) {
        return;
      }

      this.closeSelectionSummary();
    });
  }

  handleSelectionSummaryMouseDown(event: MouseEvent): void {
    // A click on a lower chip would otherwise move focus before its click handler
    // can mark the removal as internal, causing the focusout handler to close the panel.
    if ((event.target as HTMLElement | null)?.closest('.astral-chip__remove')) {
      event.preventDefault();
    }
  }

  handleSelectionSummaryKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeSelectionSummary(false, true);
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const buttons = Array.from(this.selectionSummaryPanel?.nativeElement.querySelectorAll<HTMLButtonElement>('button') ?? []);
    if (!buttons.length) {
      return;
    }

    const activeIndex = buttons.indexOf(this.elementRef.nativeElement.ownerDocument?.activeElement as HTMLButtonElement);
    const nextIndex = event.shiftKey
      ? activeIndex <= 0 ? buttons.length - 1 : activeIndex - 1
      : activeIndex < 0 || activeIndex === buttons.length - 1 ? 0 : activeIndex + 1;

    event.preventDefault();
    buttons[nextIndex].focus();
  }

  optionId(index: number): string {
    return `${this.resolvedInputId}-option-${index}`;
  }

  removeItem(item: AutoCompleteSelectionItem<TData>): void {
    const removedIndex = this.selectedItems.indexOf(item);
    this.isMovingSelectionSummaryFocus = true;
    this.toggleItem(item);
    if (!this.showSelectionSummary) {
      this.closeSelectionSummary();
      requestAnimationFrame(() => {
        this.input?.nativeElement.focus();
        this.isMovingSelectionSummaryFocus = false;
      });
      return;
    }

    requestAnimationFrame(() => {
      const buttons = this.selectionSummaryPanel?.nativeElement.querySelectorAll<HTMLButtonElement>('button');
      buttons?.item(Math.min(Math.max(removedIndex, 0), Math.max(buttons.length - 1, 0)))?.focus();
      this.isMovingSelectionSummaryFocus = false;
    });
  }

  clearAll(): void {
    if (this.control?.isReadonly || this.control?.disabled) return;
    this.selectedItems = [];
    this.closeSelectionSummary();
    this.searchControl.setValue('', { emitEvent: false });
    this.updateControlValue();
    this.clearClick.emit(this.control);
  }

  clearOtherInputs(): void {
    if (this.control?.isReadonly || this.control?.disabled || !this.control?.parent) return;
    for (const name of Object.keys(this.control.parent.controls)) {
      const sibling = this.control.parent.get(name) as FormControlEx;
      if (sibling !== this.control && !sibling?.isReadonly) sibling.setValue(undefined);
    }
    this.addonClick.emit(this.control);
  }

  private updateControlValue(): void {
    this.control.setValue(this.selectedItems.map(item => item.value));
    this.control.markAsDirty();
    this.control.markAsTouched();
  }

  private syncSearchControlDisabledState(): void {
    const isDisabled = !!this.control?.disabled;
    if (this.searchControlDisabled === isDisabled) return;

    this.searchControlDisabled = isDisabled;
    // Existing forms may change both value and disabled state with emitEvent: false.
    this.syncSelectedItemsFromControl(this.control?.value);
    if (isDisabled) {
      this.searchControl.disable({ emitEvent: false });
      this.closePanel();
      this.closeSelectionSummary();
      return;
    }

    this.searchControl.enable({ emitEvent: false });
  }

  private syncSelectedItemsFromControl(value: string[] | string | null | undefined, forceReload = false): void {
    this.selectionLoadSub.unsubscribe();
    this.selectionLoadSub = new Subscription();
    const version = ++this.selectionSyncVersion;
    const ids = (Array.isArray(value) ? value : value ? [value] : []).filter(Boolean);
    if (!ids.length) {
      this.selectedItems = [];
      return;
    }
    const existing = new Map(this.selectedItems.map(item => [item.value, item]));
    this.selectedItems = forceReload ? [] : ids.map(id => existing.get(id)).filter(Boolean) as AutoCompleteSelectionItem<TData>[];
    for (const id of ids) {
      if (this.selectedItems.some(item => item.value === id)) continue;
      this.selectionLoadSub.add(this.service.getSelectionItem(id, { selectedItem: null, options: this.serviceOptions }).subscribe(item => {
        if (version === this.selectionSyncVersion && item && !this.isSelected(item)) this.selectedItems = [...this.selectedItems, item];
      }));
    }
  }

  private loadResults(search: string, page = 1): void {
    if (!this.service) return;
    this.loadSub?.unsubscribe();
    if (page === 1) this.allPagesLoaded = false;
    this.currentSearch = search;
    this.loadSub = this.service.getSelection(search, this.serviceOptions, page).pipe(catchError(() => of([]))).subscribe(items => {
      this.searchResults = page === 1 ? items : [...this.searchResults, ...items];
      if (page === 1) {
        this.activeOptionIndex = -1;
      }
      if (this.activeOptionIndex >= this.displayItems.length) {
        this.activeOptionIndex = -1;
      }
      this.page = page;
      this.allPagesLoaded = items.length === 0;
      this.cd.markForCheck();
    });
  }

  private clearCachedResults(): void {
    this.loadSub?.unsubscribe();
    this.searchResults = [];
    this.page = 0;
    this.allPagesLoaded = false;
    this.activeOptionIndex = -1;
  }

  private syncOverlayWidth(): void {
    const shellWidth = this.controlShell?.nativeElement.getBoundingClientRect().width;
    const viewportWidth = this.elementRef.nativeElement.ownerDocument?.documentElement?.clientWidth;

    if (!shellWidth) {
      return;
    }

    this.overlayWidth = viewportWidth ? getAstralDropdownOverlayWidth(shellWidth, viewportWidth) : shellWidth;
  }

  private focusFirstSummaryAction(): void {
    this.selectionSummaryPanel?.nativeElement.querySelector<HTMLButtonElement>('button')?.focus();
  }

  private scrollActiveOptionIntoView(): void {
    if (this.activeOptionIndex < 0) {
      return;
    }

    this.panel?.nativeElement.querySelector<HTMLElement>(`#${this.optionId(this.activeOptionIndex)}`)?.scrollIntoView({ block: 'nearest' });
  }
}
